import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Space skybox shader - black background with procedural stars and sun
const skyVertexShader = `
  varying vec3 vWorldPosition;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPos.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const skyFragmentShader = `
  uniform vec3  uSunDirection;
  uniform vec3  uSunColor;
  uniform float uSunIntensity;
  uniform float uTime;
  uniform float uPlanetRadius;

  varying vec3 vWorldPosition;

  // --- hashing helpers ---------------------------------------------------
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float hash3(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
  }

  // --- star field --------------------------------------------------------
  // Tile 3-D direction space into cells; one possible star per cell.
  float starField(vec3 dir, float scale, float threshold) {
    vec3 cell = floor(dir * scale);
    vec3 frac = fract(dir * scale);

    // Random star centre within the cell
    vec3 centre = vec3(hash(cell.xy + cell.z * 31.0),
                       hash(cell.yz + cell.x * 17.0),
                       hash(cell.xz + cell.y * 53.0));

    float dist = length(frac - centre);

    // Only keep a fraction of cells as stars
    float presence = step(threshold, hash3(cell));

    // Random brightness per star
    float brightness = hash(cell.xz + cell.y * 7.0);
    brightness = 0.3 + 0.7 * brightness;

    // Twinkle
    float twinkle = sin(uTime * (1.0 + brightness * 3.0) + hash(cell.xy) * 6.28) * 0.5 + 0.5;
    brightness *= 0.7 + 0.3 * twinkle;

    // Sharp falloff
    float star = smoothstep(0.15, 0.0, dist) * presence * brightness;
    return star;
  }

  void main() {
    vec3 viewDir = normalize(vWorldPosition - cameraPosition);
    vec3 sunDir  = normalize(uSunDirection);
    // Local sun height: angle between sun and camera's surface normal
    // Negative on the dark side of the planet, positive on the lit side
    float sunHeight = dot(sunDir, normalize(cameraPosition));

    // ---------- atmosphere visibility ----------
    // In orbit, sky background is space (black + stars).
    // The <Atmosphere> component handles the blue limb glow separately.
    // Only show sky gradient when near/on the surface.
    float camDist = length(cameraPosition);
    float altRatio = camDist / max(uPlanetRadius, 1.0);
    float atmosphereMask = smoothstep(1.05, 1.0, altRatio);

    // ---------- atmospheric sky gradient ----------
    float dayAmount = smoothstep(-0.1, 0.25, sunHeight);

    float viewUp = viewDir.y;

    vec3 zenithDay = vec3(0.22, 0.45, 0.85);
    vec3 horizonDay = vec3(0.55, 0.70, 0.88);

    float sunsetAmount = smoothstep(-0.05, 0.1, sunHeight) * (1.0 - smoothstep(0.1, 0.3, sunHeight));
    vec3 zenithSunset = vec3(0.25, 0.30, 0.55);
    vec3 horizonSunset = vec3(0.85, 0.45, 0.25);

    float horizonBlend = 1.0 - smoothstep(-0.05, 0.6, viewUp);
    vec3 skyDay = mix(zenithDay, horizonDay, horizonBlend);
    vec3 skySunset = mix(zenithSunset, horizonSunset, horizonBlend);
    vec3 skyColor = mix(skyDay, skySunset, sunsetAmount);

    // Atmosphere only visible when looking toward planet
    vec3 atmosphere = skyColor * dayAmount * atmosphereMask;

    // ---------- stars at two density scales ----------
    float stars = 0.0;
    stars += starField(viewDir, 80.0,  0.92) * 0.8;
    stars += starField(viewDir, 200.0, 0.95) * 0.4;
    stars += starField(viewDir, 500.0, 0.97) * 0.15;

    float colSeed = hash(floor(viewDir.xy * 200.0));
    vec3 starColor = mix(vec3(0.8, 0.85, 1.0), vec3(1.0, 0.9, 0.75), colSeed);

    // Stars visible in space, dimmed only where atmosphere is visible
    float sunProximity = pow(max(dot(viewDir, sunDir), 0.0), 6.0);
    stars *= (1.0 - dayAmount * atmosphereMask) * (1.0 - sunProximity);

    // ---------- sun disc + glow ----------
    float sunAngle  = dot(viewDir, sunDir);
    float sunDisc   = smoothstep(0.9995, 0.99999, sunAngle);
    float sunGlow   = pow(max(sunAngle, 0.0), 64.0) * 0.4;
    float sunCorona = pow(max(sunAngle, 0.0), 12.0) * 0.15;

    vec3 sunGlowColor = mix(uSunColor, vec3(1.0, 0.5, 0.2), sunsetAmount * 0.6);
    vec3 sunContribution = sunGlowColor * (sunDisc + sunGlow + sunCorona) * uSunIntensity;

    // ---------- combine ----------
    vec3 finalColor = atmosphere + starColor * stars + sunContribution;

    finalColor = finalColor / (finalColor + vec3(1.0));

    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

interface SkyProps {
  sunPosition?: THREE.Vector3;
  radius?: number;
}

export function Sky({
  sunPosition = new THREE.Vector3(0.5, 0.3, 0.8),
  radius = 900,
}: SkyProps) {
  const meshRef = useRef<THREE.Mesh>(null);

  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader: skyVertexShader,
      fragmentShader: skyFragmentShader,
      uniforms: {
        uSunDirection: { value: sunPosition.clone().normalize() },
        uSunColor: { value: new THREE.Color('#fff5e0') },
        uSunIntensity: { value: 1.5 },
        uTime: { value: 0 },
        uPlanetRadius: { value: radius },
      },
      side: THREE.BackSide,
      depthWrite: false,
    });
  }, [sunPosition, radius]);

  useEffect(() => {
    return () => { material.dispose(); };
  }, [material]);

  // Update uniforms each frame
  useFrame((state) => {
    if (material) {
      material.uniforms.uTime.value = state.clock.elapsedTime;
      material.uniforms.uSunDirection.value.copy(sunPosition).normalize();
    }
    
    // Keep sky centered on camera
    if (meshRef.current) {
      meshRef.current.position.copy(state.camera.position);
    }
  });

  return (
    <mesh ref={meshRef}>
      <icosahedronGeometry args={[radius * 2.8, 4]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}
